import React, { useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Dashboard as TemplatesIcon } from '@mui/icons-material';
import { EntityListPanel, EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import { DesktopRecommendedArea } from '../../../shared/components/DesktopRecommendedNotice';
import TemplateEditor from './TemplateEditor';
import { makeTagGroupBy } from '../../../shared/utils/tags';
import type { RuleTemplate, RuleTemplateListItem } from '../../../types/frontend';

const OWNER_LABELS: Record<string, string> = {
  global: 'Global',
  customer: 'Customer',
};

const templateListConfig: EntityListItemConfig<RuleTemplateListItem> = {
  getPrimaryText: (t) => t.name,
  getSecondaryText: (t) => {
    const owner = OWNER_LABELS[t.owner] || 'Global';
    return `${t.category} · ${owner}`;
  },
  getId: (t) => t.template_id,
  getSearchText: (t) => `${t.name} ${t.description ?? ''} ${(t.tags ?? []).join(' ')}`,
};

const templateGroupBy = makeTagGroupBy<RuleTemplateListItem>();

interface TemplatesTabContentProps {
  customerId: string;
  customerName?: string;
  templates: RuleTemplateListItem[];
  selectedTemplate: RuleTemplate | null;
  selectedTemplateItem: RuleTemplateListItem | null;
  isNewTemplate: boolean;
  loadingTemplate: boolean;
  onSelectTemplate: (t: RuleTemplateListItem) => void;
  onNewTemplate: () => void;
  onTemplateSaved: (savedTemplate: RuleTemplate) => void;
  onDeleteTemplate: () => void;
  onDiscardTemplate: () => void;
  onUseAsRule: () => void;
}

const TemplatesTabContent: React.FC<TemplatesTabContentProps> = ({
  customerId,
  customerName,
  templates,
  selectedTemplate,
  selectedTemplateItem,
  isNewTemplate,
  loadingTemplate,
  onSelectTemplate,
  onNewTemplate,
  onTemplateSaved,
  onDeleteTemplate,
  onDiscardTemplate,
  onUseAsRule,
}) => {
  const showEditor = isNewTemplate || !!selectedTemplate;

  const allTemplateTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const t of templates) {
      for (const tag of t.tags ?? []) tagSet.add(tag);
    }
    return Array.from(tagSet).sort();
  }, [templates]);

  return (
    <DesktopRecommendedArea>
      <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel
          entities={templates}
          selectedEntity={selectedTemplateItem}
          isNew={isNewTemplate}
          saving={false}
          config={templateListConfig}
          addButtonText="New Template"
          emptyStateText="No templates"
          emptyStateHelperText="Create a template for quick rule creation"
          onSelect={onSelectTemplate}
          onAddNew={onNewTemplate}
          isLoading={false}
          searchable
          groupBy={templateGroupBy}
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {loadingTemplate ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : showEditor ? (
          <TemplateEditor
            key={isNewTemplate ? 'new' : selectedTemplate?.template_id ?? 'none'}
            customerId={customerId}
            customerName={customerName}
            template={selectedTemplate}
            isNew={isNewTemplate}
            suggestedTags={allTemplateTags}
            onSaved={onTemplateSaved}
            onDelete={onDeleteTemplate}
            onDiscard={onDiscardTemplate}
            onUseAsRule={onUseAsRule}
          />
        ) : (
          <EmptyStateMessage
            icon={TemplatesIcon}
            title="Select a template"
            description="Choose a template to edit or create a new one"
          />
        )}
      </Box>
    </DesktopRecommendedArea>
  );
};

export default TemplatesTabContent;
